import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounceState } from '@common-fe/common-fe';

import { usePlanYearStore } from '@/modules/ExpressSetup/ExpressSetupWizard/PlansWizard/PlanYear/stores';
import { Plan } from '@/modules/plan/plan.types.ts';

import { PlansPayload, usePlansStore } from '../stores/usePlans.store';

const ALREADY_EXIST_ERROR_MESSAGE = 'This plan code already used';
const EMPTY_FIELD_ERROR_MESSAGE = 'Can\'t be empty';

export const usePlanCodeActions = (planTemplate: Plan, savedPlans?: Plan[], showError?: boolean) => {
  const handleSetPlansState = usePlansStore((state) => state.setState);
  const errorsCode = usePlansStore((state) => state.errors.planCodes);

  const planYearState = usePlanYearStore((state) => state.state.planStartDate);
  const planYear = useMemo(() => planYearState?.split('/')[2], [planYearState]);

  const currentPlan = useMemo(
    () => savedPlans?.find((el) => el.id === planTemplate.id),
    [savedPlans, planTemplate.id]
  );

  const generatePlanCode = useCallback(() => `${planTemplate.type}${planYear}`, [planTemplate.type, planYear]);

  const initialPlanCode = useMemo(() => {
    if (currentPlan?.planCode) return currentPlan.planCode;
    if (planTemplate.planCode) return planTemplate.planCode;
    return generatePlanCode();
  }, [currentPlan?.planCode, planTemplate.planCode, generatePlanCode]);

  const [isEditMode, setEditMode] = useState(false);
  const { setValues, values } = useDebounceState<string>();

  const handleSetPlanCode = useCallback(() => {
    handleSetPlansState((prevState: Partial<PlansPayload>) => {
      const updatedPlans = prevState?.plans?.map((plan: Plan) =>
        plan.id === planTemplate.id
          ? { ...plan, planCode: values || undefined }
          : plan
      );

      return { ...prevState, plans: updatedPlans };
    });
  }, [handleSetPlansState, planTemplate.id, values]);

  const handleResetPlanCode = useCallback(() => {
    setValues(initialPlanCode);
  }, [initialPlanCode, setValues]);

  const isError = useMemo(() => {
    return showError && values && errorsCode?.some(error => error.toLowerCase() === values.toLowerCase());
  }, [showError, values, errorsCode]);

  const errorText = useMemo(() => {
    if (showError && !values?.trim()) return EMPTY_FIELD_ERROR_MESSAGE;
    if (isError) return ALREADY_EXIST_ERROR_MESSAGE;
    return undefined;
  }, [showError, values, isError]);

  useEffect(() => {
    setValues(initialPlanCode);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleSetPlanCode();
  }, [values, handleSetPlanCode]);

  return {
    isEditMode,
    setEditMode,
    initialPlanCode,
    values,
    errorText,
    setValues,
    handleResetPlanCode,
  };
};
